import DOMPurify from 'dompurify'

export interface TimeTranslations {
  justNow?: string
  minuteAgo?: string
  minutesAgo?: string
  hourAgo?: string
  hoursAgo?: string
  yesterday?: string
  dayAgo?: string
  daysAgo?: string
  weekAgo?: string
  weeksAgo?: string
}

const defaultTimeTranslations: TimeTranslations = {
  justNow: 'Just now',
  minuteAgo: '{{count}} minute ago',
  minutesAgo: '{{count}} minutes ago',
  hourAgo: '{{count}} hour ago',
  hoursAgo: '{{count}} hours ago',
  yesterday: 'Yesterday',
  dayAgo: '{{count}} day ago',
  daysAgo: '{{count}} days ago',
  weekAgo: '{{count}} week ago',
  weeksAgo: '{{count}} weeks ago',
}

export const formatTimestamp = (
  timestamp: string,
  translations?: TimeTranslations,
  locale = 'fr-FR'
): string => {
  if (!timestamp) return ''

  const date = new Date(timestamp)

  if (isNaN(date.getTime())) {
    return ''
  }

  const t = { ...defaultTimeTranslations, ...translations }

  const formatWithCount = (template: string, count: number) =>
    template.replace('{{count}}', String(count))

  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))

  if (diffDays === 0) {
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60))
    if (diffHours === 0) {
      const diffMinutes = Math.floor(diffMs / (1000 * 60))
      if (diffMinutes === 0) {
        return t.justNow || 'Just now'
      }
      return formatWithCount(
        diffMinutes > 1 ? t.minutesAgo || '' : t.minuteAgo || '',
        diffMinutes
      )
    }
    return formatWithCount(
      diffHours > 1 ? t.hoursAgo || '' : t.hourAgo || '',
      diffHours
    )
  } else if (diffDays === 1) {
    return t.yesterday || 'Yesterday'
  } else if (diffDays < 7) {
    return formatWithCount(
      diffDays > 1 ? t.daysAgo || '' : t.dayAgo || '',
      diffDays
    )
  } else if (diffDays < 30) {
    const weeks = Math.floor(diffDays / 7)
    return formatWithCount(
      weeks > 1 ? t.weeksAgo || '' : t.weekAgo || '',
      weeks
    )
  } else {
    return date.toLocaleDateString(locale)
  }
}

export const sanitizeHtml = (html: string): string => {
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: [
      'p',
      'br',
      'strong',
      'b',
      'em',
      'i',
      'u',
      'a',
      'ul',
      'ol',
      'li',
      'h1',
      'h2',
      'h3',
      'h4',
      'h5',
      'h6',
      'blockquote',
      'code',
      'pre',
      'mark',
      'span',
      'div',
    ],
    ALLOWED_ATTR: ['href', 'target', 'rel', 'class'],
  })
}

export const highlightText = (htmlContent: string, query: string): string => {
  if (!query.trim()) return sanitizeHtml(htmlContent)

  const escapeRegExp = (string: string) => {
    return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  }

  const regex = new RegExp(`(${escapeRegExp(query)})`, 'gi')
  const highlighted = htmlContent.replace(regex, '<mark>$1</mark>')
  return sanitizeHtml(highlighted)
}

export const getTextFromHtml = (html: string): string => {
  const tempDiv = document.createElement('div')
  tempDiv.innerHTML = html
  return tempDiv.textContent || tempDiv.innerText || ''
}
